<template>
  <OptimizationBaseDialog
    v-model="visibility"
    :optimization="props.optimization"
    :person="props.person">
    <div
      v-if="person && !props.optimization.deprecated"
      class="flex w-full flex-col gap-6">
      <SmTable
        v-if="accounts"
        ref="table"
        key-field="_id"
        :data="accounts"
        :columns="columns">
        <template #header>
          <p
            class="sm-table-header-cell px-3 font-bold"
            :class="{ 'text-red': validation == false }">
            {{ i18n.t('chooseSelectionMessage') }}:
          </p>
        </template>
        <template #account="{ row }">
          <div class="flex flex-col gap-1 py-3">
            <TextMultiline :text="row.email" />
            <TextMultiline class="text-contrast-muted">
              {{
                i18n.t('licensesCount', {
                  count: Object.keys(row.software_licenses).length,
                })
              }}
            </TextMultiline>
          </div>
        </template>

        <template #action="{ row }">
          <SmSelect
            v-model:selection="changes[row._id]"
            :options="options"
            :label="i18n.t('chooseOptions')"
            trigger-style="plain"
            dropdown-min-width="150px"
            class="cursor-pointer"
            @change="
              (option) => {
                if (!option) return
                setChange(row._id, option)
              }
            " />
        </template>
      </SmTable>
    </div>

    <!-- Loader -->
    <div
      v-else-if="loading"
      class="group mb-4 flex w-full items-center gap-3 rounded border p-4 hover:cursor-pointer">
      <SmSkeleton height="48px" width="48px" />
      <div class="flex w-1/2 flex-col gap-1">
        <SmSkeleton height="14px" />
        <SmSkeleton height="14px" />
        <SmSkeleton height="14px" width="50%" />
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else
      class="group mb-4 flex w-full items-center gap-3 rounded border p-4 hover:cursor-pointer">
      <EmptyState
        class="min-h-14"
        :text="i18n.t('noAccountFound')"
        orientation="horizontal" />
    </div>

    <!-- Footer -->
    <template #footer>
      <SmButton
        v-if="props.optimization.hidden && !props.optimization.deprecated"
        outline
        @click="emit('unhide', props.optimization._id)">
        {{ i18n.t('unhideOptimization') }}
      </SmButton>
      <SmButton
        v-else-if="!props.optimization.hidden && !props.optimization.deprecated"
        outline
        @click="emit('hide', props.optimization._id)">
        {{ i18n.t('views.optimizations.table.actions.hideOptimization') }}
      </SmButton>
      <SmButton v-if="!props.optimization.deprecated" @click="executeChanges()">
        {{ i18n.t('execute') }}
      </SmButton>
    </template>
  </OptimizationBaseDialog>
</template>

<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'
  import {
    EnrichedOptimizations,
    OptimizationStore,
  } from '@/stores/optimization/optimization.store'
  import {
    Account,
    LicenceOut,
    OptimizationSoftwareAccountDuplicateData,
  } from '@/client'
  import { Option } from '@/components/sm/SmSelect.vue'
  import { AccountStore } from '@/stores/account/account.store'
  import { sendToast } from '@/components/sm/SmNotification'
  import { Column } from '@/components/sm/SmTable.types'
  import { ComponentExposed } from 'vue-component-type-helpers'
  import SmTable from '@/components/sm/SmTable.vue'
  import { PersonStore } from '@/stores/person/person.store'

  const i18n = useI18n()

  const props = defineProps({
    optimization: {
      type: Object as PropType<EnrichedOptimizations>,
      required: true,
    },
    person: {
      type: Object as PropType<Account>,
      required: true,
    },
  })

  const visibility = defineModel({
    type: Boolean,
    required: true,
  })

  const emit = defineEmits(['hide', 'unhide'])

  const { data } = props.optimization

  // ----------------------------------------------------------------------
  // Load Data
  // ----------------------------------------------------------------------

  const accounts = ref<LicenceOut[] | null>(null)
  const personObj = ref<Account>()
  const loading = ref(false)

  onMounted(() => {
    loadAccounts()
    loadPerson()
  })

  function loadPerson() {
    loading.value = true
    const info = data as OptimizationSoftwareAccountDuplicateData
    PersonStore.getPersonById(info.account_id).then((res) => {
      if (res) {
        personObj.value = res
      }
      loading.value = false
    })
  }

  function loadAccounts() {
    const info = data as OptimizationSoftwareAccountDuplicateData

    AccountStore.getAccounts({
      ids: info.software_account_ids,
    }).then((res) => {
      if (res) {
        accounts.value = res
      }
    })
  }

  // ----------------------------------------------------------------------
  // Selection
  // ----------------------------------------------------------------------

  const options = ref<Option<string>[]>([
    {
      label: i18n.t('general.keep'),
      value: 'keep',
    },
    {
      label: i18n.t('general.remove'),
      value: 'remove',
    },
  ])

  const changes = ref<Record<string, Option<string>[]>>({})

  function setChange(licenseId: string, option: Option<string>[]) {
    changes.value[licenseId] = option
  }

  // ----------------------------------------------------------------------
  // Table
  // ----------------------------------------------------------------------

  const table = ref<ComponentExposed<typeof SmTable<LicenceOut>>>()

  const columns = computed<Column<LicenceOut>[]>(() => [
    {
      key: 'account',
      label: i18n.t('account'),
      sortable: false,
      width: 1,
      loader: true,
    },
    {
      key: 'action',
      label: '',
      width: 'auto',
      sortable: false,
    },
  ])
  // ----------------------------------------------------------------------
  // Execute changes
  // ----------------------------------------------------------------------

  const validation = ref<boolean>()

  function executeChanges() {
    validation.value = validate()

    if (validation.value) {
      for (const id in changes.value) {
        if (changes.value[id][0].value === 'remove') {
          AccountStore.deleteAccount(id)
        }
      }

      sendToast(
        i18n.t(
          'notifications.applicationsDetailView.licences.saveLicencesSuccess.message'
        ),
        undefined,
        'success'
      )

      OptimizationStore.refreshOptimizationCacheFromApi({}, false, false)
      visibility.value = false
    } else {
      sendToast(
        i18n.t(
          'notifications.applicationsDetailView.licences.saveLicencesFailed.message'
        ),
        undefined,
        'error'
      )
      console.error(`Accounts could not be saved`)
    }
  }

  function validate(): boolean {
    if (accounts.value === null) return false
    return Object.keys(changes.value).length === accounts.value?.length
  }
</script>
